import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'

import { requirePermission } from '@/utils/auth'
import UnexpectedEventsList from '@/components/UnexpectedEvents/UnexpectedEventsList'
import { UnexpectedEventType } from '@/api/main/types.gen'
import { UNEXPECTED_EVENT_TYPES } from '@/components/UnexpectedEvents/eventTypes'

export type UnexpectedEventsTab = 'pending' | 'archived'

export type UnexpectedEventsSearch = {
  tab?: UnexpectedEventsTab
  district?: Array<string>
  route?: string
  type?: UnexpectedEventType
}

const parseDistricts = (raw: unknown): Array<string> | undefined => {
  if (Array.isArray(raw)) {
    const arr = raw.filter((d): d is string => typeof d === 'string' && d !== '')
    return arr.length > 0 ? arr : undefined
  }
  if (typeof raw === 'string' && raw !== '') {
    // Backward-compat with the old single-value URL form.
    return [raw]
  }
  return undefined
}

export const Route = createFileRoute('/unexpected-events')({
  beforeLoad: () => requirePermission('unexpected_events', 'read'),
  component: UnexpectedEventsLayoutComponent,
  validateSearch: (raw: Record<string, unknown>): UnexpectedEventsSearch => {
    const district = parseDistricts(raw.district)
    const route = typeof raw.route === 'string' && raw.route !== '' ? raw.route : undefined
    const type =
      typeof raw.type === 'string' && (UNEXPECTED_EVENT_TYPES as ReadonlyArray<string>).includes(raw.type)
        ? (raw.type as UnexpectedEventType)
        : undefined
    const tab = raw.tab === 'archived' ? ('archived' as const) : undefined
    const out: UnexpectedEventsSearch = {}
    if (tab) out.tab = tab
    if (district) out.district = district
    if (route) out.route = route
    if (type) out.type = type
    return out
  },
})

function UnexpectedEventsLayoutComponent() {
  const location = useLocation()
  const isExactPath = location.pathname === '/unexpected-events' || location.pathname === '/unexpected-events/'

  if (isExactPath) {
    return <UnexpectedEventsList />
  }

  return <Outlet />
}
